import React, { useState, useEffect, useMemo } from "react";
import {
  collection, doc, getDoc, getDocs,
  serverTimestamp, runTransaction, query, where
} from "firebase/firestore";
import { auth, db } from "./firebase";
import { signInAnonymously } from "firebase/auth";
import {
  X, AlertCircle, CheckCircle, Clock,
  Users, Mail, MapPin, ShieldCheck, Star
} from "lucide-react";

/* ── inline avatar ── */
function makeDoctorAvatar(name) {
  const initials = (name || "D").split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  const colors = ["#3b82f6", "#6366f1", "#8b5cf6", "#10b981"];
  const bg = colors[(name || "").length % colors.length];
  return `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200">
      <rect width="200" height="200" fill="${bg}"/>
      <text x="50%" y="50%" text-anchor="middle" dy=".35em" fill="#fff" font-size="80" font-family="Arial" font-weight="bold">${initials}</text>
    </svg>`
  )}`;
}

/* ── date/time helpers ── */
const pad2 = n => String(n).padStart(2, "0");

function formatDateYMD(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function weekdayKeyFromDate(d) {
  return ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"][d.getDay()];
}

function toMinutes(hhmm) {
  const [h, m] = (hhmm || "00:00").split(":").map(Number);
  return h * 60 + m;
}

function fromMinutes(mins) {
  return `${pad2(Math.floor(mins / 60))}:${pad2(mins % 60)}`;
}

function formatSlotLabel(ymd, hhmm) {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return `${dt.toLocaleDateString("en-US", { weekday: "short" })} ${hhmm}`;
}

function makeGuestDateList(workingHours, daysAhead = 30) {
  const out = [];
  const today = new Date();
  for (let i = 0; i < daysAhead; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const key = weekdayKeyFromDate(d);
    const cfg = workingHours?.[key];
    if (!cfg?.enabled) continue;
    out.push({
      date: formatDateYMD(d),
      label: d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }),
      fullDate: d,
      cfg,
    });
  }
  return out;
}

async function fetchLockedTimesForDate(doctorId, ymd) {
  const snap = await getDocs(
    query(
      collection(db, "locks"),
      where("doctorId", "==", doctorId),
      where("scheduledDate", "==", ymd)
    )
  );
  const locked = new Set();
  snap.forEach(d => {
    const x = d.data();
    if (x.scheduledTime && (x.status || "pending") !== "cancelled") {
      locked.add(x.scheduledTime);
    }
  });
  return locked;
}

function buildTimeSlotsForDay(cfg, durationMin, lockedTimes, isToday) {
  const slots = [];
  const now = new Date();
  const start = toMinutes(cfg.start || "09:00");
  const end = toMinutes(cfg.end || "17:00");
  const curMin = isToday ? now.getHours() * 60 + now.getMinutes() : -1;
  const dur = Math.max(5, Number(durationMin) || 30);

  for (let t = start; t + dur <= end; t += dur) {
    if (isToday && t <= curMin) continue;
    const hhmm = fromMinutes(t);
    if (lockedTimes.has(hhmm)) continue;
    slots.push(hhmm);
  }
  return slots;
}

/* ════════════════════════════════════════════
   COMPONENT
   ════════════════════════════════════════════ */
export default function GuestBookingSheet({ doctor, language, onClose, onSwitchToAuth }) {
  const isSq = language === "sq";

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  const [selectedService, setSelectedService] = useState(null);
  const [doctorServices, setDoctorServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [timeSlotsLoading, setTimeSlotsLoading] = useState(false);

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  /* available dates */
  const availableDates = useMemo(
    () => makeGuestDateList(doctor.workingHours || {}, 30),
    [doctor.workingHours]
  );

  /* fetch services */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setServicesLoading(true);
        const snap = await getDoc(doc(db, "doctors", doctor.id));
        if (alive && snap.exists()) {
          const services = Array.isArray(snap.data().services) ? snap.data().services : [];
          setDoctorServices(services);
          if (services.length === 1) setSelectedService(services[0]);
        }
      } catch {
        if (alive) setDoctorServices([]);
      } finally {
        if (alive) setServicesLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [doctor.id]);

  /* fetch time slots when date or service changes */
  useEffect(() => {
    if (!selectedDate) return;
    let alive = true;
    (async () => {
      setTimeSlotsLoading(true);
      setSelectedTime(null);
      try {
        const locked = await fetchLockedTimesForDate(doctor.id, selectedDate.date);
        const dur = selectedService
          ? (parseInt(selectedService.durationMinutes || doctor.durationMinutes || 30))
          : (doctor.durationMinutes || 30);
        const isToday = selectedDate.date === formatDateYMD(new Date());
        const slots = buildTimeSlotsForDay(selectedDate.cfg, dur, locked, isToday);
        if (alive) setTimeSlots(slots);
      } catch {
        if (alive) setTimeSlots([]);
      } finally {
        if (alive) setTimeSlotsLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [selectedDate, doctor.id, doctor.durationMinutes, selectedService]);

  /* derived slot */
  const selectedSlot = selectedDate && selectedTime
    ? { date: selectedDate.date, time: selectedTime, label: formatSlotLabel(selectedDate.date, selectedTime) }
    : null;

  /* progress */
  const steps = [
    !!selectedService || doctorServices.length === 0,
    !!selectedDate,
    !!selectedTime,
    !!name.trim() && !!phone.trim(),
  ];
  const completed = steps.filter(Boolean).length;
  const pct = Math.round((completed / steps.length) * 100);

  /* submit */
  const submit = async () => {
   if (!name.trim() || !phone.trim() || !email.trim()) {
  setError(isSq ? "Plotëso emrin, telefonin dhe email-in." : "Please fill in your name, phone and email.");
  return;
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
if (!emailRegex.test(email.trim())) {
  setError(isSq ? "Vendos një email të vlefshëm." : "Please enter a valid email address.");
  return;
}
    if (doctorServices.length > 0 && !selectedService) {
      setError(isSq ? "Zgjidh një shërbim." : "Please select a service.");
      return;
    }
    if (!selectedSlot) {
      setError(isSq ? "Zgjidh datën dhe orarin." : "Please select a date and time.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      let uid = auth.currentUser?.uid;
      if (!uid) {
        const cred = await signInAnonymously(auth);
        uid = cred.user.uid;
      }

      const { date: ymd, time: hhmm } = selectedSlot;
      const lockId = `${doctor.id}_${ymd}_${hhmm}`;
      const lockRef = doc(db, "locks", lockId);
      const appointmentRef = doc(collection(db, "appointments"));

      const appointmentData = {
        patientId: uid,
        patientInfo: { name: name.trim(), email: email.trim() || "", phone: phone.trim() },
        doctorId: doctor.id,
        doctorInfo: {
          name: doctor.name || "Doctor",
          specialty: doctor.specialtyLabel || doctor.specialtyKey || "General",
          location: doctor.location || "",
        },
        serviceId: selectedService?.id || "",
        serviceName: selectedService?.title || doctor.specialtyLabel || "Consultation",
        servicePrice: selectedService?.price || doctor.feeText || "",
        serviceDuration: selectedService?.duration || `${doctor.durationMinutes || 30} minutes`,
        appointmentType: "in-person",
        scheduledDate: ymd,
        scheduledTime: hhmm,
        scheduledAt: new Date(`${ymd}T${hhmm}:00`),
        reason: "", notes: "",
        isGuest: true,
        status: "pending",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await runTransaction(db, async tx => {
        const lockSnap = await tx.get(lockRef);
        if (lockSnap.exists() && (lockSnap.data().status || "pending") !== "cancelled") {
          throw new Error(isSq ? "Ky orar u mor. Zgjidh një tjetër." : "Slot just taken. Pick another.");
        }
        lockSnap.exists()
          ? tx.update(lockRef, {
              doctorId: doctor.id, scheduledDate: ymd, scheduledTime: hhmm,
              appointmentType: "in-person", status: "pending", isGuest: true,
              guestName: name.trim(), guestPhone: phone.trim(),
              guestEmail: email.trim() || null, patientId: uid,
              updatedAt: serverTimestamp(),
            })
          : tx.set(lockRef, {
              doctorId: doctor.id, scheduledDate: ymd, scheduledTime: hhmm,
              appointmentType: "in-person", status: "pending", isGuest: true,
              guestName: name.trim(), guestPhone: phone.trim(),
              guestEmail: email.trim() || null, patientId: uid,
              createdAt: serverTimestamp(),
            });
        tx.set(appointmentRef, appointmentData);
      });

      setSuccess(true);
    } catch (e) {
      setError(e?.message || (isSq ? "Rezervimi dështoi. Provo sërish." : "Booking failed. Try again."));
    } finally {
      setLoading(false);
    }
  };

  /* ── SUCCESS ── */
  if (success) {
    return (
      <>
        <div className="mdk-sheet-backdrop" onClick={onClose} />
        <section className="mdk-sheet" role="dialog" aria-modal="true">
          <div className="mdk-sheet-b" style={{ textAlign: "center", padding: "40px 24px" }}>
            <CheckCircle size={56} color="#10b981" style={{ marginBottom: 16 }} />
            <h2 style={{ fontSize: 20, fontWeight: 900, marginBottom: 8 }}>
              {isSq ? "Rezervimi u krye!" : "Booking confirmed!"}
            </h2>
            <p style={{ color: "rgba(11,15,24,.55)", fontSize: 14, fontWeight: 900, marginBottom: 8 }}>
              {isSq
                ? `Takimi me ${doctor.name} për ${selectedService?.title || "konsultim"}`
                : `Appointment with ${doctor.name} for ${selectedService?.title || "consultation"}`}
            </p>
            <p style={{ color: "#6366f1", fontSize: 15, fontWeight: 900, marginBottom: 24 }}>
              {selectedSlot?.label}
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
              <button className="mdk-btn primary" onClick={onClose}>
                {isSq ? "Mbyll" : "Close"}
              </button>
              <button className="mdk-btn" onClick={onSwitchToAuth}>
                {isSq ? "Krijo llogari" : "Create account"}
              </button>
            </div>
          </div>
        </section>
      </>
    );
  }

  /* ── FORM ── */
  return (
    <>
      <div className="mdk-sheet-backdrop" onClick={onClose} />
      <section className="mdk-sheet" role="dialog" aria-modal="true">

        {/* ── HEADER ── */}
        <header className="mdk-sheet-h" style={{ flexDirection: "column", alignItems: "stretch", gap: 0, padding: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px 12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <img
                src={doctor.photoURL || makeDoctorAvatar(doctor.name)}
                alt={doctor.name}
                style={{ width: 44, height: 44, borderRadius: 14, objectFit: "cover", border: "2px solid rgba(99,102,241,.2)" }}
                onError={e => { e.target.src = makeDoctorAvatar(doctor.name); }}
              />
              <div>
                <div style={{ fontSize: 15, fontWeight: 900, color: "rgba(11,15,24,.9)" }}>{doctor.name}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(11,15,24,.5)" }}>
                  {doctor.specialtyLabel}{doctor.city ? ` • ${doctor.city}` : ""}
                  {doctor.verified && <span style={{ marginLeft: 6, color: "rgb(99,102,241)" }}>✓ {isSq ? "Verifikuar" : "Verified"}</span>}
                </div>
              </div>
            </div>
            <button className="mdk-sheet-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
          </div>

          {/* CTA banner */}
          <div style={{
            margin: "0 16px 14px",
            background: "linear-gradient(135deg, rgba(99,102,241,.12), rgba(139,92,246,.08))",
            border: "1px solid rgba(99,102,241,.2)",
            borderRadius: 14, padding: "12px 16px",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
          }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 900, color: "rgba(11,15,24,.85)" }}>
                {isSq ? "🗓 Rezervo vizitën tani" : "🗓 Book your visit now"}
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(11,15,24,.45)", marginTop: 2 }}>
                {isSq ? "Pa llogari • Pa kartelë • Falas" : "No account • No card • Free"}
              </div>
            </div>
            {doctor.feeText && (
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 15, fontWeight: 900, color: "rgb(99,102,241)" }}>{doctor.feeText}</div>
                <div style={{ fontSize: 11, color: "rgba(11,15,24,.4)", fontWeight: 700 }}>
                  {isSq ? "çmimi i vizitës" : "visit fee"}
                </div>
              </div>
            )}
          </div>
        </header>

        <div className="mdk-sheet-b">

          {/* ── PROGRESS ── */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, fontWeight: 900, color: "rgba(11,15,24,.45)", marginBottom: 6 }}>
              <span>{isSq ? "Plotëso rezervimin" : "Complete your booking"}</span>
              <span style={{ color: pct === 100 ? "#10b981" : "rgb(99,102,241)" }}>{pct}%</span>
            </div>
            <div style={{ height: 4, borderRadius: 2, background: "rgba(11,15,24,.08)", overflow: "hidden" }}>
              <div style={{
                height: "100%", borderRadius: 2, width: `${pct}%`,
                background: pct === 100 ? "linear-gradient(90deg,#10b981,#059669)" : "linear-gradient(90deg,rgb(99,102,241),rgb(139,92,246))",
                transition: "width 0.4s cubic-bezier(0.34,1.56,0.64,1)",
              }} />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
              {[
                { label: isSq ? "Shërbimi" : "Service", done: steps[0] },
                { label: isSq ? "Data" : "Date", done: steps[1] },
                { label: isSq ? "Ora" : "Time", done: steps[2] },
                { label: isSq ? "Kontakti" : "Contact", done: steps[3] },
              ].map((step, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 900, color: step.done ? "#10b981" : "rgba(11,15,24,.4)" }}>
                  <div style={{ width: 16, height: 16, borderRadius: "50%", background: step.done ? "#10b981" : "rgba(11,15,24,.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "#fff", fontWeight: 900, transition: "background 0.3s" }}>
                    {step.done ? "✓" : i + 1}
                  </div>
                  {step.label}
                </div>
              ))}
            </div>
          </div>

          {error && (
            <div className="mdk-msg err" style={{ marginBottom: 14 }}>
              <AlertCircle size={16} /><span>{error}</span>
            </div>
          )}

          {/* ── SERVICES ── */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 900, marginBottom: 8, color: "rgba(11,15,24,.7)" }}>
              {isSq ? "Zgjidh shërbimin:" : "Select a service:"}
            </div>
            {servicesLoading ? (
              <div style={{ fontSize: 13, color: "rgba(11,15,24,.4)", fontWeight: 900 }}>{isSq ? "Duke ngarkuar..." : "Loading..."}</div>
            ) : doctorServices.length === 0 ? (
              <div style={{ fontSize: 13, color: "rgba(11,15,24,.45)", fontWeight: 900, background: "rgba(99,102,241,.05)", borderRadius: 10, padding: "10px 14px" }}>
                {isSq ? "Konsultim i përgjithshëm" : "General consultation"}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {doctorServices.map((service, i) => {
                  const active = selectedService?.id === service.id || (!service.id && selectedService?.title === service.title);
                  return (
                    <button
                      key={service.id || i}
                      type="button"
                      onClick={() => setSelectedService(service)}
                      style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "12px 14px", borderRadius: 10, textAlign: "left", gap: 8, cursor: "pointer",
                        border: `2px solid ${active ? "rgba(99,102,241,.4)" : "rgba(11,15,24,.1)"}`,
                        background: active ? "rgba(99,102,241,.08)" : "rgba(255,255,255,.9)",
                      }}
                    >
                      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <span style={{ fontSize: 14, fontWeight: 900, color: "rgba(11,15,24,.85)" }}>{service.title}</span>
                        {service.duration && (
                          <span style={{ fontSize: 12, color: "rgba(11,15,24,.45)", fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
                            <Clock size={11} />{service.duration}
                          </span>
                        )}
                        {service.description && (
                          <span style={{ fontSize: 12, color: "rgba(11,15,24,.5)", fontWeight: 700 }}>{service.description}</span>
                        )}
                      </div>
                      {service.price && (
                        <span style={{ fontSize: 13, fontWeight: 900, color: "#059669", background: "#dcfce7", padding: "4px 10px", borderRadius: 20, whiteSpace: "nowrap", flexShrink: 0 }}>
                          {service.price}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── DATE PICKER ── */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 900, marginBottom: 8, color: "rgba(11,15,24,.7)" }}>
              {isSq ? "Zgjidh datën:" : "Select a date:"}
            </div>
            {availableDates.length === 0 ? (
              <p style={{ fontSize: 13, color: "rgba(11,15,24,.45)", fontWeight: 900 }}>
                {isSq ? "Nuk ka ditë të disponueshme." : "No available days."}
              </p>
            ) : (
              <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 6 }}>
                {availableDates.map((d, i) => {
                  const active = selectedDate?.date === d.date;
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setSelectedDate(d)}
                      style={{
                        flexShrink: 0,
                        display: "flex", flexDirection: "column", alignItems: "center",
                        padding: "8px 12px", borderRadius: 12, cursor: "pointer",
                        border: `2px solid ${active ? "#6366f1" : "#e0e7ff"}`,
                        background: active ? "#6366f1" : "#f8f7ff",
                        minWidth: 54,
                      }}
                    >
                      <span style={{ fontSize: 10, fontWeight: 800, color: active ? "rgba(255,255,255,.8)" : "#94a3b8" }}>
                        {d.fullDate.toLocaleDateString(undefined, { weekday: "short" })}
                      </span>
                      <span style={{ fontSize: 17, fontWeight: 900, color: active ? "#fff" : "#1e1b4b", lineHeight: 1.2 }}>
                        {d.fullDate.getDate()}
                      </span>
                      <span style={{ fontSize: 10, fontWeight: 700, color: active ? "rgba(255,255,255,.7)" : "#94a3b8" }}>
                        {d.fullDate.toLocaleDateString(undefined, { month: "short" })}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── TIME PICKER ── */}
          {selectedDate && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 13, fontWeight: 900, marginBottom: 8, color: "rgba(11,15,24,.7)" }}>
                {isSq ? "Zgjidh orarin:" : "Select a time:"}
              </div>
              {timeSlotsLoading ? (
                <div style={{ fontSize: 13, color: "rgba(11,15,24,.4)", fontWeight: 900 }}>{isSq ? "Duke ngarkuar..." : "Loading..."}</div>
              ) : timeSlots.length === 0 ? (
                <p style={{ fontSize: 13, color: "rgba(11,15,24,.45)", fontWeight: 900 }}>
                  {isSq ? "Nuk ka orare për këtë ditë." : "No slots for this day."}
                </p>
              ) : (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {timeSlots.map((hhmm, i) => {
                    const active = selectedTime === hhmm;
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setSelectedTime(hhmm)}
                        style={{
                          padding: "7px 13px", borderRadius: 10,
                          border: `2px solid ${active ? "#6366f1" : "#e0e7ff"}`,
                          background: active ? "#6366f1" : "#f8f7ff",
                          fontSize: 13, fontWeight: 800,
                          color: active ? "#fff" : "#4338ca",
                          cursor: "pointer",
                          display: "flex", alignItems: "center", gap: 4,
                        }}
                      >
                        <Clock size={11} style={{ opacity: .7 }} />
                        {hhmm}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── GUEST FIELDS ── */}
          <div className="mdk-field2">
            <div style={{ position: "relative" }}>
              <Users size={16} style={{ position: "absolute", left: 12, top: 14, color: "rgba(11,15,24,.35)" }} />
              <input
                className="mdk-input"
                style={{ paddingLeft: 38 }}
                type="text"
                placeholder={isSq ? "Emri dhe mbiemri *" : "Full name *"}
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </div>
            <div style={{ position: "relative" }}>
              <MapPin size={16} style={{ position: "absolute", left: 12, top: 14, color: "rgba(11,15,24,.35)" }} />
              <input
                className="mdk-input"
                style={{ paddingLeft: 38 }}
                type="tel"
                placeholder={isSq ? "Numri i telefonit *" : "Phone number *"}
                value={phone}
                onChange={e => setPhone(e.target.value)}
              />
            </div>
            <div style={{ position: "relative" }}>
              <Mail size={16} style={{ position: "absolute", left: 12, top: 14, color: "rgba(11,15,24,.35)" }} />
              <input
                className="mdk-input"
                style={{ paddingLeft: 38 }}
                type="email"
              placeholder={isSq ? "Email *" : "Email *"}
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </div>

            {/* Selected summary */}
            {selectedSlot && (
              <div style={{
                background: "rgba(99,102,241,.06)", border: "1px solid rgba(99,102,241,.15)",
                borderRadius: 12, padding: "10px 14px",
                display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
              }}>
                <div style={{ fontSize: 13, fontWeight: 900, color: "rgba(11,15,24,.7)" }}>
                  {isSq ? "Takimi juaj:" : "Your booking:"}
                </div>
                <div style={{ fontSize: 13, fontWeight: 900, color: "rgb(99,102,241)" }}>
                  {selectedSlot.label}
                </div>
              </div>
            )}

            {/* Confirm button */}
            <button
              className="mdk-btn primary"
              type="button"
              onClick={submit}
              disabled={loading}
              style={{
                marginTop: 4, padding: "16px 24px", fontSize: 16, fontWeight: 900,
                borderRadius: 14, width: "100%", justifyContent: "center",
                background: "linear-gradient(135deg, rgb(99,102,241), rgb(139,92,246))",
                boxShadow: "0 8px 24px rgba(99,102,241,.35)",
                letterSpacing: "0.01em",
              }}
            >
              {loading
                ? (isSq ? "Duke rezervuar..." : "Booking...")
                : `✓ ${isSq ? "Konfirmo rezervimin" : "Confirm Booking"}${selectedSlot ? ` — ${selectedSlot.label}` : ""}`}
            </button>

            {/* Trust line */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, fontSize: 12, fontWeight: 700, color: "rgba(11,15,24,.4)", marginTop: 4 }}>
              <span>🔒 {isSq ? "E sigurt" : "Secure"}</span>
              <span>⚡ {isSq ? "Konfirmim i shpejtë" : "Fast confirmation"}</span>
              <span>📞 {isSq ? "Do ju kontaktojmë" : "We'll call you"}</span>
            </div>

            {/* Sign in link */}
            <div style={{ textAlign: "center", fontSize: 13, color: "rgba(11,15,24,.5)", fontWeight: 900 }}>
              {isSq ? "Keni llogari?" : "Have an account?"}{" "}
              <button
                type="button"
                onClick={onSwitchToAuth}
                style={{ border: "none", background: "none", color: "rgb(99,102,241)", fontWeight: 1000, cursor: "pointer", padding: 0 }}
              >
                {isSq ? "Hyr" : "Sign in"}
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}